import { useMemo } from "react"
import type { EventLogItem } from "../types"
import { useTheme } from "../theme/ThemeProvider"

type LogFeedProps = {
  items: EventLogItem[]
}

const categoryColors: Record<EventLogItem["category"], string> = {
  invoice: "var(--accent)",
  payment: "#31d0aa",
  reminder: "#ff7a1a",
  system: "#9090ff",
}

export function LogFeed({ items }: LogFeedProps) {
  const { theme } = useTheme()
  const isFairy = theme === "fairy"
  const cardBackground = isFairy ? "rgba(255,255,255,0.95)" : "rgba(18,18,20,0.8)"
  const cardBorder = isFairy ? "1px solid rgba(46,214,181,0.3)" : "1px solid rgba(255,43,79,0.2)"
  const titleColor = isFairy ? "#1f2d42" : "var(--text)"
  const metaColor = isFairy ? "#4b5a6d" : "var(--muted)"
  const sorted = useMemo(() => [...items].sort((a, b) => (a.at < b.at ? 1 : -1)), [items])

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      {sorted.map((item) => (
        <article
          key={item.id}
          style={{
            display: "grid",
            gridTemplateColumns: "120px 1fr",
            gap: "12px",
            background: cardBackground,
            border: cardBorder,
            padding: "18px 20px",
            position: "relative",
            boxShadow: isFairy ? "0 12px 24px rgba(46,214,181,0.12)" : "0 0 18px rgba(255,43,79,0.16)",
          }}
        >
          <span className="small" style={{ color: metaColor, letterSpacing: "0.12em" }}>
            {formatTime(item.at)}
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <div className="mono" style={{ display: "flex", alignItems: "center", gap: "10px", color: titleColor }}>
              <span
                aria-hidden
                style={{
                  display: "inline-flex",
                  width: "10px",
                  height: "10px",
                  border: `1px solid ${categoryColors[item.category]}`,
                  background: categoryColors[item.category],
                  boxShadow: `0 0 10px ${categoryColors[item.category]}`,
                }}
              />
              <span>{item.summary}</span>
            </div>
            <p style={{ margin: 0, color: metaColor }}>{item.detail}</p>
          </div>
        </article>
      ))}
      {sorted.length === 0 ? (
        <div className="small" style={{ color: metaColor }}>
          Activity from your agent will show up here.
        </div>
      ) : null}
    </div>
  )
}

function formatTime(iso: string) {
  try {
    const date = new Date(iso)
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

export default LogFeed
